import { ApiError, fetchForecast } from '../api/client'
import type { ForecastBundle } from '../api/types'
import { canonical } from '../geolocation'
import { whileVisible } from '../visibility'
import { checkConnection } from './connection.svelte'
import { setLastLocation } from './prefs.svelte'

/**
 * Why the point is on screen, which decides the camera and the phone sheet
 * (`MapView`, `MobileLayout`; plan §5.7):
 *
 * - `click`, `search`, `geo`: the user asked, by the map menu, a search (a ZIP,
 *   a place name or a saved place) or the locate button.
 * - `restore`: put there at boot (the last viewed place, or the last fix while
 *   locating). Jumps; the app is still starting.
 * - `here`: a boot fix that moved. Flies, as a visible correction.
 * - `here-refresh`: a fix on return that moved. Marker only, never the camera:
 *   the user may have panned somewhere on purpose.
 * - `link`: a deep link, `/?at=lat,lon`.
 */
export type PointSource = 'click' | 'search' | 'geo' | 'restore' | 'here' | 'here-refresh' | 'link'

export interface SelectedPoint {
  lat: number
  lon: number
  label?: string
  zip?: string
  source: PointSource
  /**
   * What the point *is*, as opposed to how it got here: `'here'` when it is
   * the phone's own position, a saved place's id when it was opened as that
   * place, absent for a plain point. The headers read this rather than
   * guessing from `source`, which says how the point arrived and not what it
   * is. (A plain point within 1 km of a saved place is still that place:
   * `savedFor()` asks the distance when there is no id.)
   */
  place?: 'here' | string
}

/**
 * How a `select()` ended. `unreachable` is the request never landing (offline,
 * the server down); `failed` is the server answering with an error, which is
 * an answer about the point (outside NWS coverage, NWS failing behind us).
 */
export type LoadOutcome = 'loaded' | 'failed' | 'unreachable' | 'superseded'

export type LoadStatus = 'idle' | 'loading' | 'ready' | 'error'

export const location = $state({
  selected: null as SelectedPoint | null,
  bundle: null as ForecastBundle | null,
  /**
   * The point `bundle` was loaded for. A failed load keeps the previous
   * bundle, so this can differ from `selected`, and anything that names the
   * point from the bundle has to ask.
   */
  bundleFor: null as { lat: number; lon: number } | null,
  status: 'idle' as LoadStatus,
  error: null as string | null,
  fetchedAt: null as Date | null,
  /**
   * Bumped by every selection. Anything that acts on the selection later — a
   * boot fix landing after the user searched, a status note about the point
   * the boot chose — holds the value it saw and gives up when it has moved.
   * `requestSeq`'s rule, one level up.
   */
  generation: 0,
})

/**
 * The selected point's name, or null when it has none: the caller's label (ZIP
 * lookups have one), else the city NWS names for the grid point.
 *
 * The bundle is consulted only when it is *this* point's. A failed load keeps
 * the previous bundle on screen, and the place header is the only place the
 * name appears, so without the check a tap on the Pacific, or any point still
 * loading, is labelled with the city looked at before it.
 */
export function placeName(): string | null {
  const point = location.selected
  if (!point) return null
  if (point.label) return point.label
  const bundle = selectedBundle()
  return bundle ? (labelFrom(bundle) ?? null) : null
}

/**
 * The bundle, if it is the selected point's; null while a new point loads and
 * after its load failed. What a *header* reads, because a header claims to be
 * about the point it names. The panels below keep showing `location.bundle`,
 * on the rule that a stale forecast with an error line beats an empty sheet.
 */
export function selectedBundle(): ForecastBundle | null {
  const point = location.selected
  const owner = location.bundleFor
  if (!point || !owner || owner.lat !== point.lat || owner.lon !== point.lon) return null
  return location.bundle
}

// Assigning to `$state` stores a *proxy*, so `location.selected === point` is
// never true afterwards. Staleness is tracked with a counter instead.
let requestSeq = 0

/**
 * Point the app at `point` and load its forecast.
 *
 * The coordinates are canonicalised to the server's 4 decimal places first, so
 * one place is one URL for the service worker's forecast cache and for every
 * server-side cache behind it.
 *
 * A failed load keeps the previous bundle on screen: a stale forecast with an
 * error line beats an empty sidebar, and offline is the common case on a phone.
 */
export async function select(point: SelectedPoint): Promise<LoadOutcome> {
  location.selected = { ...point, lat: canonical(point.lat), lon: canonical(point.lon) }
  location.generation++
  return load(location.selected)
}

/**
 * Put back a point whose bundle is still the one on screen, with no request.
 * For an automatic fix whose forecast could not be fetched: the failed load
 * kept the previous point's bundle, and leaving the marker at the new point
 * over the old point's forecast is a header naming one city over another's
 * weather. Refused (false) when the bundle on screen is not `point`'s.
 */
export function restoreShown(point: SelectedPoint): boolean {
  const owner = location.bundleFor
  if (!location.bundle || !owner || owner.lat !== point.lat || owner.lon !== point.lon) return false
  location.selected = point
  location.generation++
  location.status = 'ready'
  location.error = null
  return true
}

/**
 * How old a bundle may get before it is asked for again. Half the backend's
 * `WEATHER_FORECAST_TTL`, because the bundle also carries the alerts, which
 * the backend holds for thirty seconds — and a warning issued while the phone
 * was in a pocket is the one thing on this screen that cannot wait.
 */
export const FORECAST_MAX_AGE_MS = 5 * 60_000
/** How often a visible page checks that age. The check is free; a request is
 *  made only when the bundle has actually aged out. */
const CHECK_MS = 60_000

/**
 * Re-load the selected point's forecast if the one on screen is older than
 * `maxAgeMs`, without moving anything.
 *
 * Not `select()`: that assigns `location.selected`, which `MapView` follows
 * with a camera move, so a background refresh would yank the map back to the
 * forecast point from wherever the user had panned. The point is the same
 * point; only the bundle is new.
 */
export async function refreshForecast(maxAgeMs: number = FORECAST_MAX_AGE_MS): Promise<void> {
  const point = location.selected
  if (!point || location.status === 'loading') return
  // `fetchedAt` is the response's own Date header, so a bundle the service
  // worker served from its cache while offline reads as old and is retried.
  const at = location.fetchedAt?.getTime() ?? 0
  if (Date.now() - at < maxAgeMs) return
  await load(point)
}

/**
 * Keep the forecast current for as long as the page is open. Returns a
 * disposer. Until this existed the bundle was fetched once per selection and
 * never again, so a phone that reopened the app showed whatever it had loaded
 * the last time somebody searched — "Updated 3 hr ago" — until they searched
 * again.
 */
export function startForecastRefresh(): () => void {
  return whileVisible(() => void refreshForecast(), CHECK_MS)
}

async function load(point: SelectedPoint): Promise<LoadOutcome> {
  const seq = ++requestSeq
  location.status = 'loading'
  location.error = null

  try {
    const { bundle, fetchedAt } = await fetchForecast(point.lat, point.lon)
    // A newer selection may have superseded this one while it was in flight.
    if (seq !== requestSeq) return 'superseded'
    location.bundle = bundle
    location.bundleFor = { lat: point.lat, lon: point.lon }
    location.fetchedAt = fetchedAt
    location.status = 'ready'
    setLastLocation({
      lat: point.lat,
      lon: point.lon,
      label: point.label ?? labelFrom(bundle),
      zip: point.zip,
    })
    return 'loaded'
  } catch (e) {
    if (isAbort(e)) return 'superseded' // A newer request owns the state now.
    if (seq !== requestSeq) return 'superseded'
    location.status = 'error'
    location.error = e instanceof ApiError ? e.detail : `Network error: ${errorText(e)}`
    // An ApiError is our server answering — NWS failing behind it, most often.
    // Anything else is the request never landing, and the banner should say
    // so now rather than at the next heartbeat.
    if (e instanceof ApiError) return 'failed'
    void checkConnection()
    return 'unreachable'
  }
}

function labelFrom(bundle: ForecastBundle): string | undefined {
  const parts = [bundle.location.city, bundle.location.state].filter(Boolean)
  return parts.length ? parts.join(', ') : undefined
}

function isAbort(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError'
}

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}
